import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import { PushNotifications } from '@capacitor/push-notifications'
import { watch, type WatchStopHandle } from 'vue'
import { HeaderNames } from '@/@vxapp/@Enums/PHP/HTTP/HeaderNames'
import { Endpoint } from '@/@vxapp/@Enums/Endpoint'
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'
import { useAuthStore } from '@/@vxapp/Auth/useAuthStore'
import { createRuntimeUuid } from '@/@core/utils/runtimeUuid'
import { NativeAppEventName } from '@/native/nativeApp'
import { deleteNativePushApi, postNativePushApi } from '@/native/nativePushApiClient'
import { APP_BUILD_INFO } from '@/utils/buildInfo'

const log = createLogger('NativePushRegistration')

/* eslint-disable no-unused-vars -- Core ESLint flags TypeScript enum contract members. */
enum NativePushEndpoint {
  Installation = 'Kernal/Users/PushInstallations',
  SelfTest = 'Kernal/Users/PushInstallations/SelfTest',
}

enum NativePushPlatform {
  Android = 'android',
}

enum NativeAuthAction {
  Logout = 'logout',
  ProcessAuthResponse = 'processAuthResponse',
}

export enum NativePushSelfTestFailure {
  DeliveryFailed = 'PUSH_DELIVERY_FAILED',
  InstallationNotFound = 'PUSH_INSTALLATION_NOT_FOUND',
  NotAuthenticated = 'NOT_AUTHENTICATED',
  NotNative = 'NOT_NATIVE',
  NotificationsNotConfigured = 'PUSH_NOTIFICATIONS_NOT_CONFIGURED',
  RateLimited = 'PUSH_SELF_TEST_RATE_LIMITED',
  UnexpectedResponse = 'UNEXPECTED_RESPONSE',
}
/* eslint-enable no-unused-vars */

interface NativePushRegistrationEventDetail {
  platform: string
  token: string
}

interface NativePushPermissionEventDetail {
  receive: string
}

interface NativeAuthSnapshot {
  headers: Record<string, string>
  identity: string
}

interface NativePushInstallationResponse {
  serviceData?: {
    pushInstallation?: {
      appVersion?: string
      installationId?: string
      platform?: string
      registeredAt?: string
      revoked?: boolean
      status?: string
    }
  }
}

interface NativePushSelfTestResponse {
  serviceData?: {
    pushNotificationTest?: {
      accepted?: boolean
      installationId?: string
    }
  }
}

export type NativePushSelfTestResult =
  | { accepted: true; installationId: string }
  | {
      accepted: false
      reason: NativePushSelfTestFailure
      retryAfterSeconds?: number
    }

const INSTALLATION_ID_PREFERENCE = 'vxapp.native.push.installation-id'

const UUID_V4_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const SEMVER_PATTERN = /^(\d+)\.(\d+)(?:\.(\d+))?([+-][a-z\d.-]+)?$/i
const MAXIMUM_PUSH_TOKEN_LENGTH = 4096
const MAXIMUM_FRESH_TOKEN_REGISTRATION_ATTEMPTS = 4
const FRESH_TOKEN_RETRY_DELAY_MS = 1_000
const REQUEST_TIMEOUT_MS = 15_000

// eslint-disable-next-line no-unused-vars -- The parameter documents the DOM listener contract.
type NativeEventListener = (event: Event) => void

let activeToken: string | null = null
let cachedInstallationId: string | null | undefined
let freshTokenRegistrationAttempts = 0
let freshTokenRequested = false
let freshTokenBlockedIdentity: string | null = null
let freshTokenRetryTimeout: number | null = null
let invalidatedIdentity: string | null = null
let invalidatedToken: string | null = null
let installationIdLoadPromise: Promise<string | null> | null = null
let installationIdCreatePromise: Promise<string> | null = null
let isInitialized = false
let localTokenResetPromise: Promise<boolean> | null = null
let localTokenResetRequired = false
let needsFreshToken = false
let observedAuthSnapshot: NativeAuthSnapshot | null = null
let permissionGranted: boolean | null = null
let registeredKey: string | null = null
let runtimeGeneration = 0
let synchronizationPromise: Promise<void> | null = null
let synchronizationRequested = false
let stopAuthActionSubscription: (() => void) | null = null
let stopAuthWatcher: WatchStopHandle | null = null

const nativeEventListeners: Array<{
  eventName: NativeAppEventName
  listener: NativeEventListener
}> = []

const clearFreshTokenRetry = (): void => {
  if (freshTokenRetryTimeout !== null) {
    window.clearTimeout(freshTokenRetryTimeout)
    freshTokenRetryTimeout = null
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const isNativeAndroid = (): boolean =>
  Capacitor.isNativePlatform() && Capacitor.getPlatform() === NativePushPlatform.Android

const isValidInstallationId = (value: unknown): value is string =>
  typeof value === 'string' && UUID_V4_PATTERN.test(value)

const isValidPushToken = (value: unknown): value is string =>
  typeof value === 'string' &&
  value.length > 0 &&
  value.length <= MAXIMUM_PUSH_TOKEN_LENGTH &&
  !Array.from(value).some(character => {
    const characterCode = character.charCodeAt(0)

    return /\s/.test(character) || characterCode < 32 || characterCode === 127
  })

export const normalizeNativeAppVersion = (version: unknown): string => {
  if (typeof version !== 'string') {
    return '0.0.0'
  }

  const match = version.trim().match(SEMVER_PATTERN)
  if (!match) {
    return '0.0.0'
  }

  const [, major, minor, patch = '0', suffix = ''] = match

  return `${major}.${minor}.${patch}${suffix}`
}

const getNativeAppVersion = (): string => normalizeNativeAppVersion(APP_BUILD_INFO.version)

const resolveApiUrl = (endpoint: NativePushEndpoint): string | null => {
  try {
    const baseUrl = new URL(Endpoint.Vxapp.Api.url)

    if (
      baseUrl.protocol !== 'https:' ||
      baseUrl.username ||
      baseUrl.password ||
      baseUrl.search ||
      baseUrl.hash
    ) {
      log.error('Native push API calls require an HTTPS API origin')

      return null
    }

    const normalizedBaseUrl = baseUrl.toString().endsWith('/')
      ? baseUrl.toString()
      : `${baseUrl.toString()}/`

    return new URL(endpoint, normalizedBaseUrl).toString()
  } catch {
    log.error('Native push API URL is invalid')

    return null
  }
}

const readHeader = (headers: unknown, expectedName: HeaderNames): string | null => {
  if (!isRecord(headers)) {
    return null
  }

  const matchingEntry = Object.entries(headers).find(
    ([headerName]) => headerName.toLowerCase() === expectedName.toLowerCase()
  )

  return typeof matchingEntry?.[1] === 'string' && matchingEntry[1].length > 0
    ? matchingEntry[1]
    : null
}

const captureAuthSnapshot = (): NativeAuthSnapshot | null => {
  const authStore = useAuthStore()
  const authorization = readHeader(authStore.headers, HeaderNames.AUTH_JWT)
  const apiGuid = readHeader(authStore.headers, HeaderNames.API_GUID)
  const userData: unknown = authStore.userData
  const userGuid = isRecord(userData) ? userData.userGUID : null

  if (
    !authStore.isAuthenticated ||
    !authStore.isUser ||
    !authorization ||
    !apiGuid ||
    typeof userGuid !== 'string' ||
    userGuid.length === 0
  ) {
    return null
  }

  return {
    headers: {
      [HeaderNames.AUTH_JWT]: authorization,
      [HeaderNames.API_GUID]: apiGuid,
    },
    identity: `${apiGuid}:${userGuid}`,
  }
}

const loadInstallationId = async (): Promise<string | null> => {
  if (cachedInstallationId !== undefined) {
    return cachedInstallationId
  }

  if (!installationIdLoadPromise) {
    installationIdLoadPromise = (async () => {
      const storedPreference = await Preferences.get({ key: INSTALLATION_ID_PREFERENCE })

      if (isValidInstallationId(storedPreference.value)) {
        cachedInstallationId = storedPreference.value

        return storedPreference.value
      }

      if (storedPreference.value !== null) {
        await Preferences.remove({ key: INSTALLATION_ID_PREFERENCE })
      }

      cachedInstallationId = null

      return null
    })()
  }

  try {
    return await installationIdLoadPromise
  } catch (error: unknown) {
    cachedInstallationId = undefined
    installationIdLoadPromise = null

    throw error
  }
}

const getOrCreateInstallationId = async (): Promise<string> => {
  const existingInstallationId = await loadInstallationId()
  if (existingInstallationId) {
    return existingInstallationId
  }

  if (!installationIdCreatePromise) {
    installationIdCreatePromise = (async () => {
      const installationId = createRuntimeUuid()

      if (!isValidInstallationId(installationId)) {
        throw new Error('Unable to create a valid native installation identifier')
      }

      await Preferences.set({
        key: INSTALLATION_ID_PREFERENCE,
        value: installationId,
      })
      cachedInstallationId = installationId

      return installationId
    })()
  }

  try {
    return await installationIdCreatePromise
  } catch (error: unknown) {
    installationIdCreatePromise = null

    throw error
  }
}

const extractRequestFailure = (
  error: unknown
): {
  reason: NativePushSelfTestFailure
  responseCode: number | null
  retryAfterSeconds?: number
} => {
  const errorRecord = isRecord(error) ? error : {}
  const response = isRecord(errorRecord.response) ? errorRecord.response : {}
  const data = isRecord(response.data) ? response.data : {}
  const serviceData = isRecord(data.serviceData) ? data.serviceData : {}
  const responseCode = typeof response.status === 'number' ? response.status : null
  const apiReason = typeof data.error === 'string' ? data.error : null

  const retryAfterSeconds =
    typeof serviceData.retryAfterSeconds === 'number' ? serviceData.retryAfterSeconds : undefined

  const supportedReasons = new Set<string>([
    NativePushSelfTestFailure.DeliveryFailed,
    NativePushSelfTestFailure.InstallationNotFound,
    NativePushSelfTestFailure.NotificationsNotConfigured,
    NativePushSelfTestFailure.RateLimited,
  ])

  return {
    reason:
      apiReason && supportedReasons.has(apiReason)
        ? (apiReason as NativePushSelfTestFailure)
        : NativePushSelfTestFailure.UnexpectedResponse,
    responseCode,
    ...(retryAfterSeconds === undefined ? {} : { retryAfterSeconds }),
  }
}

const revokeInstallation = async (
  authSnapshot: NativeAuthSnapshot,
  timeoutMs = REQUEST_TIMEOUT_MS
): Promise<boolean> => {
  try {
    const installationId = await loadInstallationId()
    const apiUrl = resolveApiUrl(NativePushEndpoint.Installation)

    if (!installationId || !apiUrl) {
      return false
    }

    const response = await deleteNativePushApi<NativePushInstallationResponse>(
      apiUrl,
      { installationId },
      {
        headers: authSnapshot.headers,
        timeoutMs,
      }
    )

    const revokedInstallation = response.data.serviceData?.pushInstallation

    return (
      revokedInstallation?.installationId === installationId && revokedInstallation.revoked === true
    )
  } catch (error: unknown) {
    const failure = extractRequestFailure(error)

    log.warn('Native push installation revocation failed', {
      responseCode: failure.responseCode,
    })

    return false
  }
}

const requestFreshPushToken = async (generation: number): Promise<void> => {
  const authSnapshot = captureAuthSnapshot()

  if (
    generation !== runtimeGeneration ||
    !needsFreshToken ||
    freshTokenRequested ||
    freshTokenRetryTimeout !== null ||
    permissionGranted !== true ||
    !authSnapshot ||
    authSnapshot.identity === freshTokenBlockedIdentity
  ) {
    return
  }

  freshTokenRequested = true
  freshTokenRegistrationAttempts += 1

  try {
    await PushNotifications.register()
  } catch (error: unknown) {
    freshTokenRequested = false
    log.warn('Native push token re-registration failed', error)
    scheduleFreshPushTokenRetry(generation)
  }
}

const resetLocalPushToken = (generation: number): Promise<boolean> => {
  if (!localTokenResetRequired) {
    return Promise.resolve(true)
  }

  if (localTokenResetPromise) {
    return localTokenResetPromise
  }

  const requestedReset = (async () => {
    try {
      await PushNotifications.unregister()
    } catch (error: unknown) {
      log.warn('Native push token invalidation failed', error)

      return false
    }

    if (generation !== runtimeGeneration) {
      return false
    }

    localTokenResetRequired = false
    freshTokenRequested = false
    await requestFreshPushToken(generation)

    return true
  })()

  localTokenResetPromise = requestedReset
  void requestedReset.finally(() => {
    if (localTokenResetPromise === requestedReset) {
      localTokenResetPromise = null
    }
  })

  return requestedReset
}

const invalidateLocalPushIdentity = (
  authSnapshot: NativeAuthSnapshot | null,
  revokeTimeoutMs = REQUEST_TIMEOUT_MS,
  requireIdentityChange = true
): void => {
  const identity = authSnapshot?.identity ?? 'anonymous'

  if (needsFreshToken && invalidatedIdentity === identity) {
    return
  }

  invalidatedIdentity = identity
  invalidatedToken = activeToken ?? invalidatedToken
  activeToken = null
  registeredKey = null
  clearFreshTokenRetry()
  freshTokenRegistrationAttempts = 0
  freshTokenRequested = false
  freshTokenBlockedIdentity = requireIdentityChange ? (authSnapshot?.identity ?? null) : null
  localTokenResetRequired = true
  needsFreshToken = true

  const generation = runtimeGeneration

  void resetLocalPushToken(generation)
  if (authSnapshot) {
    void revokeInstallation(authSnapshot, revokeTimeoutMs)
  }
}

const registerInstallation = async (generation: number): Promise<void> => {
  const authSnapshot = captureAuthSnapshot()
  const registrationToken = activeToken

  if (!authSnapshot || !registrationToken || permissionGranted !== true) {
    return
  }

  const installationId = await getOrCreateInstallationId()
  const appVersion = getNativeAppVersion()
  const registrationKey = `${authSnapshot.identity}:${appVersion}:${registrationToken}`
  const apiUrl = resolveApiUrl(NativePushEndpoint.Installation)

  if (
    generation !== runtimeGeneration ||
    registrationKey === registeredKey ||
    registrationToken !== activeToken ||
    authSnapshot.identity !== captureAuthSnapshot()?.identity ||
    !apiUrl
  ) {
    return
  }

  try {
    const response = await postNativePushApi<NativePushInstallationResponse>(
      apiUrl,
      {
        appVersion,
        installationId,
        platform: NativePushPlatform.Android,
        token: registrationToken,
      },
      {
        headers: authSnapshot.headers,
        timeoutMs: REQUEST_TIMEOUT_MS,
      }
    )

    const registeredInstallation = response.data.serviceData?.pushInstallation

    if (
      generation === runtimeGeneration &&
      registrationToken === activeToken &&
      registeredInstallation?.installationId === installationId &&
      registeredInstallation.status === 'active'
    ) {
      registeredKey = registrationKey
      log.info('Native push installation synchronized')

      return
    }

    log.warn('Native push registration returned an unexpected response')
  } catch (error: unknown) {
    const failure = extractRequestFailure(error)

    log.warn('Native push installation synchronization failed', {
      responseCode: failure.responseCode,
    })
  }
}

const synchronizeDesiredState = async (generation: number): Promise<void> => {
  const authSnapshot = captureAuthSnapshot()

  if (localTokenResetRequired) {
    await resetLocalPushToken(generation)

    return
  }

  if (needsFreshToken) {
    await requestFreshPushToken(generation)

    return
  }

  if (!authSnapshot) {
    return
  }

  if (permissionGranted === false) {
    registeredKey = null
    await revokeInstallation(authSnapshot)

    return
  }

  await registerInstallation(generation)
}

const requestSynchronization = (): Promise<void> => {
  synchronizationRequested = true

  if (!synchronizationPromise) {
    const generation = runtimeGeneration

    const requestedSynchronization = (async () => {
      while (synchronizationRequested && generation === runtimeGeneration) {
        synchronizationRequested = false

        try {
          await synchronizeDesiredState(generation)
        } catch {
          log.warn('Native push desired-state synchronization failed')
        }
      }
    })()

    synchronizationPromise = requestedSynchronization
    void requestedSynchronization.finally(() => {
      if (synchronizationPromise === requestedSynchronization) {
        synchronizationPromise = null
      }
    })
  }

  return synchronizationPromise
}

function scheduleFreshPushTokenRetry(generation: number): void {
  if (
    generation !== runtimeGeneration ||
    freshTokenRetryTimeout !== null ||
    freshTokenRegistrationAttempts >= MAXIMUM_FRESH_TOKEN_REGISTRATION_ATTEMPTS
  ) {
    return
  }

  freshTokenRetryTimeout = window.setTimeout(() => {
    freshTokenRetryTimeout = null

    if (generation === runtimeGeneration && needsFreshToken) {
      void requestSynchronization()
    }
  }, FRESH_TOKEN_RETRY_DELAY_MS)
}

const registerWindowListener = (
  eventName: NativeAppEventName,
  listener: NativeEventListener
): void => {
  window.addEventListener(eventName, listener)
  nativeEventListeners.push({ eventName, listener })
}

const handlePushRegistration = (event: Event): void => {
  const detail = (event as CustomEvent<NativePushRegistrationEventDetail>).detail

  if (detail?.platform !== NativePushPlatform.Android || !isValidPushToken(detail.token)) {
    log.warn('Ignored an invalid native push registration event')

    return
  }

  if (localTokenResetRequired || localTokenResetPromise) {
    log.warn('Ignored a native push token emitted during local invalidation')

    return
  }

  if (needsFreshToken && detail.token === invalidatedToken) {
    freshTokenRequested = false
    log.warn('Ignored a stale native push token after an identity change')
    scheduleFreshPushTokenRetry(runtimeGeneration)

    return
  }

  if (needsFreshToken) {
    clearFreshTokenRetry()
    freshTokenRegistrationAttempts = 0
    needsFreshToken = false
    freshTokenRequested = false
    freshTokenBlockedIdentity = null
    invalidatedIdentity = null
    invalidatedToken = null
  }

  activeToken = detail.token
  void requestSynchronization()
}

const handlePushRegistrationError = (): void => {
  if (!needsFreshToken) {
    return
  }

  freshTokenRequested = false
  log.warn('Native push token refresh reported a registration error')
  scheduleFreshPushTokenRetry(runtimeGeneration)
}

const handlePushPermissionChange = (event: Event): void => {
  const detail = (event as CustomEvent<NativePushPermissionEventDetail>).detail

  permissionGranted = detail?.receive === 'granted'
  if (!permissionGranted) {
    invalidateLocalPushIdentity(captureAuthSnapshot(), REQUEST_TIMEOUT_MS, false)
  }

  void requestSynchronization()
}

const registerAuthLifecycle = (): void => {
  const authStore = useAuthStore()

  observedAuthSnapshot = captureAuthSnapshot()

  stopAuthWatcher = watch(
    () => [
      authStore.isAuthenticated,
      authStore.isUser,
      authStore.api,
      authStore.token,
      isRecord(authStore.userData as unknown)
        ? (authStore.userData as unknown as Record<string, unknown>).userGUID
        : null,
    ],
    () => {
      const previousAuthSnapshot = observedAuthSnapshot
      const nextAuthSnapshot = captureAuthSnapshot()

      observedAuthSnapshot = nextAuthSnapshot
      if (previousAuthSnapshot && previousAuthSnapshot.identity !== nextAuthSnapshot?.identity) {
        invalidateLocalPushIdentity(previousAuthSnapshot)
      }

      void requestSynchronization()
    }
  )

  stopAuthActionSubscription = authStore.$onAction(({ after, name }) => {
    const previousAuthSnapshot = captureAuthSnapshot()

    if (name === NativeAuthAction.Logout && previousAuthSnapshot) {
      invalidateLocalPushIdentity(previousAuthSnapshot, 2_000)

      return
    }

    if (name === NativeAuthAction.ProcessAuthResponse && previousAuthSnapshot) {
      after(() => {
        const nextAuthSnapshot = captureAuthSnapshot()

        if (nextAuthSnapshot?.identity !== previousAuthSnapshot.identity) {
          observedAuthSnapshot = nextAuthSnapshot
          invalidateLocalPushIdentity(previousAuthSnapshot)
          void requestSynchronization()
        }
      })
    }
  })
}

export const initializeNativePushRegistration = (): void => {
  if (isInitialized || !isNativeAndroid()) {
    return
  }

  isInitialized = true
  runtimeGeneration += 1

  registerWindowListener(NativeAppEventName.PushRegistration, handlePushRegistration)
  registerWindowListener(NativeAppEventName.PushRegistrationError, handlePushRegistrationError)
  registerWindowListener(NativeAppEventName.PushPermissionChanged, handlePushPermissionChange)
  registerAuthLifecycle()
}

export const revokeNativePushInstallation = async (): Promise<boolean> => {
  if (!isNativeAndroid()) {
    return false
  }

  const authSnapshot = captureAuthSnapshot()
  if (!authSnapshot) {
    return false
  }

  registeredKey = null

  return revokeInstallation(authSnapshot)
}

export const sendNativePushSelfTest = async (): Promise<NativePushSelfTestResult> => {
  if (!isNativeAndroid()) {
    return { accepted: false, reason: NativePushSelfTestFailure.NotNative }
  }

  const authSnapshot = captureAuthSnapshot()
  if (!authSnapshot) {
    return { accepted: false, reason: NativePushSelfTestFailure.NotAuthenticated }
  }

  try {
    const installationId = await loadInstallationId()
    const apiUrl = resolveApiUrl(NativePushEndpoint.SelfTest)

    if (!installationId || !apiUrl) {
      return {
        accepted: false,
        reason: NativePushSelfTestFailure.InstallationNotFound,
      }
    }

    const response = await postNativePushApi<NativePushSelfTestResponse>(
      apiUrl,
      { installationId },
      {
        headers: authSnapshot.headers,
        timeoutMs: REQUEST_TIMEOUT_MS,
      }
    )

    const selfTest = response.data.serviceData?.pushNotificationTest

    if (selfTest?.accepted === true && selfTest.installationId === installationId) {
      return { accepted: true, installationId }
    }

    return { accepted: false, reason: NativePushSelfTestFailure.UnexpectedResponse }
  } catch (error: unknown) {
    const failure = extractRequestFailure(error)

    return {
      accepted: false,
      reason: failure.reason,
      ...(failure.retryAfterSeconds === undefined
        ? {}
        : { retryAfterSeconds: failure.retryAfterSeconds }),
    }
  }
}

export const disposeNativePushRegistration = (): void => {
  runtimeGeneration += 1
  nativeEventListeners.splice(0).forEach(({ eventName, listener }) => {
    window.removeEventListener(eventName, listener)
  })
  stopAuthActionSubscription?.()
  stopAuthActionSubscription = null
  stopAuthWatcher?.()
  stopAuthWatcher = null
  activeToken = null
  clearFreshTokenRetry()
  freshTokenBlockedIdentity = null
  freshTokenRegistrationAttempts = 0
  freshTokenRequested = false
  invalidatedIdentity = null
  invalidatedToken = null
  isInitialized = false
  localTokenResetPromise = null
  localTokenResetRequired = false
  needsFreshToken = false
  observedAuthSnapshot = null
  permissionGranted = null
  registeredKey = null
  synchronizationRequested = false
  synchronizationPromise = null
  cachedInstallationId = undefined
  installationIdLoadPromise = null
  installationIdCreatePromise = null
}
